import React, { useState, useMemo } from 'react';
import { FilterElementAttributes } from '../types/FilterElementAttributes';
import { Range, getTrackBackground } from 'react-range';
import { IRenderThumbParams, IRenderTrackParams } from 'react-range/lib/types';
import styled from 'styled-components';
import { getColor } from '../../../styling/mixins/colors';
import { text } from '../../../styling/mixins/typography';

interface RenderTrackProps {
  $values: number[];
  $min: number;
  $max: number;
}

const StyledRenderThumb = styled.div`
  ${text('medium')}
  color: ${getColor('grey', 900)};
  height: 32px;
  width: 42px;
  border-radius: 4px;
  background-color: ${getColor('white')};
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 6px ${getColor('grey', 600)};
`;

const StyledRenderTrackWrapper = styled.div`
  height: 36px;
  display: flex;
  width: 100%;
`;

const StyledRenderTrack = styled.div<RenderTrackProps>`
  height: 5px;
  width: 100%;
  border-radius: 4px;
  background: ${(p) =>
    getTrackBackground({
      values: p.$values,
      colors: ['#ccc', '#000000', '#ccc'],
      min: p.$min,
      max: p.$max,
    })};
  align-self: center;
`;

const FilterRangeSlider = ({ filter, onChange }: FilterElementAttributes) => {
  const [values, setValues] = useState<number[]>([
    parseInt(
      filter.options.length == 4 ? filter.options[2]?.id ?? '1000000' : filter.options[0]?.id ?? '1000000'
    ),
    parseInt(
      filter.options.length == 4 ? filter.options[3]?.id ?? '1000000' : filter.options[1]?.id ?? '1000000'
    ),
  ]);
  const [onChangeTimer, setOnChangeTimer] = useState<NodeJS.Timeout>();

  // Store the initial min and max value
  const minValue = useMemo(() => parseInt(filter.options[0]?.id ?? '1000000'), []);
  const maxValue = useMemo(() => parseInt(filter.options[1]?.id ?? '1000000'), []);

  const onRangeChanged = (values: number[]) => {
    setValues(values);

    if (onChange) {
      if (onChangeTimer) {
        clearTimeout(onChangeTimer);
      }

      // Debounce onChange
      setOnChangeTimer(
        setTimeout(() => {
          onChange(filter.id, values);
        }, 350)
      );
    }
  };

  const renderThumb = ({ props, isDragged, value }: IRenderThumbParams) => (
    <StyledRenderThumb {...props} style={{ ...props.style }}>
      <div>{value}</div>
    </StyledRenderThumb>
  );

  const renderTrack = ({ props, children }: IRenderTrackParams) => (
    <StyledRenderTrackWrapper
      onMouseDown={props.onMouseDown}
      onTouchStart={props.onTouchStart}
      style={{ ...props.style }}
    >
      <StyledRenderTrack
        ref={props.ref}
        $values={values}
        $min={minValue}
        $max={maxValue}
      >
        {children}
      </StyledRenderTrack>
    </StyledRenderTrackWrapper>
    );

  if (minValue >= maxValue) return null;

  return (
    <Range
      values={values}
      min={minValue}
      max={maxValue}
      onChange={onRangeChanged}
      renderThumb={renderThumb}
      renderTrack={renderTrack}
    />
  );
};

export default FilterRangeSlider;
